import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarMonthIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { MovimientoCartera } from '../../services/carteraService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  accentColorArqueoCartera,
  ArqueoCarteraDrillDown,
  ArqueoCarteraGrupo,
  ArqueoCarteraPorFecha,
  ArqueoCarteraPorMcp,
  ArqueoCarteraPorTipoPago,
  ArqueoCarteraPorTipoFormulario,
  ArqueoCarteraResumen,
  calcularArqueoCartera,
} from '../../utils/arqueoCartera';

interface ArqueoCarteraDialogProps {
  open: boolean;
  onClose: () => void;
  onDrillDown?: (filtro: ArqueoCarteraDrillDown) => void;
  movimientos: MovimientoCartera[];
  carteraLabel?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  saldoActual?: number;
}

const TAB_KEYS = ['mcp', 'tipoPago', 'tipo', 'usuario', 'entidad', 'fecha'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const tabConfig: Record<TabKey, { label: string; icon: React.ReactElement }> = {
  mcp: { label: 'Medios', icon: <AccountBalanceWalletIcon fontSize="small" /> },
  tipoPago: { label: 'Tipo de pago', icon: <PaymentIcon fontSize="small" /> },
  tipo: { label: 'Tipos', icon: <DescriptionIcon fontSize="small" /> },
  usuario: { label: 'Usuarios', icon: <PersonIcon fontSize="small" /> },
  entidad: { label: 'Entidades', icon: <BusinessIcon fontSize="small" /> },
  fecha: { label: 'Fechas', icon: <CalendarMonthIcon fontSize="small" /> },
};

const KpiCard: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <Card sx={{ bgcolor: color, color: '#fff', textAlign: 'center', py: 1.5, borderRadius: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography variant="caption">{label}</Typography>
  </Card>
);

const MontosGrupo: React.FC<{ grupo: ArqueoCarteraGrupo }> = ({ grupo }) => (
  <Stack spacing={0.5}>
    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
      {formatCurrency(grupo.saldo)}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Saldo neto
    </Typography>
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
        Ing. {formatCurrency(grupo.totalIngresos)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
        Egr. {formatCurrency(grupo.totalEgresos)}
      </Typography>
    </Stack>
    <Typography variant="caption" color="text.secondary">
      {grupo.cantidadMovimientos} movimiento{grupo.cantidadMovimientos !== 1 ? 's' : ''}
    </Typography>
  </Stack>
);

const TarjetaArqueo: React.FC<{
  titulo: string;
  subtitulo?: string;
  accent: string;
  grupo: ArqueoCarteraGrupo;
  onClick?: () => void;
}> = ({ titulo, subtitulo, accent, grupo, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    sx={{
      height: '100%',
      borderLeft: 4,
      borderLeftColor: accent,
      borderRadius: 2,
      boxShadow: 'none',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      '&:hover': onClick
        ? {
            boxShadow: 2,
            transform: 'translateY(-1px)',
          }
        : undefined,
      '&:focus-visible': onClick
        ? {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          }
        : undefined,
    }}
  >
    <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="body2" fontWeight={600} noWrap title={titulo}>
        {titulo}
      </Typography>
      {subtitulo && (
        <Typography variant="caption" color="text.secondary" noWrap title={subtitulo}>
          {subtitulo}
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        <MontosGrupo grupo={grupo} />
      </Box>
    </CardContent>
  </Card>
);

const GrillaTarjetasArqueo: React.FC<{
  vacio: string;
  items: {
    key: string;
    titulo: string;
    subtitulo?: string;
    accentSeed: string;
    grupo: ArqueoCarteraGrupo;
    drillDown?: ArqueoCarteraDrillDown;
  }[];
  onDrillDown?: (filtro: ArqueoCarteraDrillDown) => void;
}> = ({ vacio, items, onDrillDown }) => {
  if (items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{vacio}</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={1.5}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item.key}>
          <TarjetaArqueo
            titulo={item.titulo}
            subtitulo={item.subtitulo}
            accent={accentColorArqueoCartera(item.accentSeed)}
            grupo={item.grupo}
            onClick={
              onDrillDown && item.drillDown
                ? () => onDrillDown(item.drillDown!)
                : undefined
            }
          />
        </Grid>
      ))}
    </Grid>
  );
};

const ArqueoCarteraDialog: React.FC<ArqueoCarteraDialogProps> = ({
  open,
  onClose,
  onDrillDown,
  movimientos,
  carteraLabel,
  fechaDesde,
  fechaHasta,
  saldoActual,
}) => {
  const [tab, setTab] = useState<TabKey>('mcp');

  const arqueo: ArqueoCarteraResumen = useMemo(
    () => calcularArqueoCartera(movimientos),
    [movimientos]
  );

  const conteos: Record<TabKey, number> = {
    mcp: arqueo.porMcp.length,
    tipoPago: arqueo.porTipoPago.length,
    tipo: arqueo.porTipoFormulario.length,
    usuario: arqueo.porUsuario.length,
    entidad: arqueo.porEntidad.length,
    fecha: arqueo.porFecha.length,
  };

  const periodoLabel =
    fechaDesde && fechaHasta
      ? `${formatDate(fechaDesde)} — ${formatDate(fechaHasta)}`
      : 'Período seleccionado';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Resumen de arqueo
          </Typography>
          {carteraLabel && (
            <Typography variant="body2" color="text.secondary">
              {carteraLabel} · {periodoLabel}
            </Typography>
          )}
        </Stack>
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <KpiCard label="Total ingresos" value={formatCurrency(arqueo.totalIngresos)} color="#4caf50" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard label="Total egresos" value={formatCurrency(arqueo.totalEgresos)} color="#f44336" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard label="Saldo neto período" value={formatCurrency(arqueo.saldoNeto)} color="#ff9800" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label={saldoActual != null ? 'Saldo actual cartera' : 'Movimientos'}
              value={saldoActual != null ? formatCurrency(saldoActual) : String(arqueo.cantidadMovimientos)}
              color="#1976d2"
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Tabs
            value={tab}
            onChange={(_, value: TabKey) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
          >
            {TAB_KEYS.map((key) => (
              <Tab
                key={key}
                value={key}
                icon={tabConfig[key].icon}
                iconPosition="start"
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{tabConfig[key].label}</span>
                    <Chip label={conteos[key]} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Stack>
                }
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
            ))}
          </Tabs>

          <Box sx={{ p: 2, maxHeight: 480, overflow: 'auto' }}>
            {onDrillDown && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Hacé clic en una tarjeta para filtrar la grilla principal.
              </Typography>
            )}

            {tab === 'mcp' && (
              <GrillaTarjetasArqueo
                vacio="No hay movimientos por medio de cobro/pago en el período."
                onDrillDown={onDrillDown}
                items={arqueo.porMcp.map((item: ArqueoCarteraPorMcp) => ({
                  key: item.mcp,
                  titulo: item.mcp,
                  accentSeed: item.mcp,
                  grupo: item,
                  drillDown: { tipo: 'mcp', valor: item.mcp, etiqueta: item.mcp },
                }))}
              />
            )}

            {tab === 'tipoPago' && (
              <GrillaTarjetasArqueo
                vacio="No hay movimientos por tipo de pago en el período."
                onDrillDown={onDrillDown}
                items={arqueo.porTipoPago.map((item: ArqueoCarteraPorTipoPago) => ({
                  key: item.tipoPago,
                  titulo: item.descripcion,
                  subtitulo: item.tipoPago !== item.descripcion ? item.tipoPago : undefined,
                  accentSeed: item.tipoPago,
                  grupo: item,
                  drillDown: { tipo: 'tipoPago', valor: item.tipoPago, etiqueta: item.descripcion },
                }))}
              />
            )}

            {tab === 'tipo' && (
              <GrillaTarjetasArqueo
                vacio="No hay movimientos por tipo de formulario en el período."
                onDrillDown={onDrillDown}
                items={arqueo.porTipoFormulario.map((item: ArqueoCarteraPorTipoFormulario) => ({
                  key: item.nroTipoFormulario,
                  titulo: item.nombreTipoFormulario,
                  subtitulo:
                    item.nroTipoFormulario !== item.nombreTipoFormulario ? item.nroTipoFormulario : undefined,
                  accentSeed: item.nroTipoFormulario,
                  grupo: item,
                  drillDown: {
                    tipo: 'tipoFormulario',
                    valor: item.nroTipoFormulario,
                    etiqueta: item.nombreTipoFormulario,
                  },
                }))}
              />
            )}

            {tab === 'usuario' && (
              <GrillaTarjetasArqueo
                vacio="No hay movimientos por usuario en el período."
                onDrillDown={onDrillDown}
                items={arqueo.porUsuario.map((item) => ({
                  key: item.usuario,
                  titulo: item.usuario,
                  accentSeed: item.usuario,
                  grupo: item,
                  drillDown: { tipo: 'usuario', valor: item.usuario, etiqueta: item.usuario },
                }))}
              />
            )}

            {tab === 'entidad' && (
              <GrillaTarjetasArqueo
                vacio="No hay movimientos por entidad en el período."
                onDrillDown={onDrillDown}
                items={arqueo.porEntidad.map((item) => ({
                  key: item.nombre,
                  titulo: item.nombre,
                  accentSeed: item.nombre,
                  grupo: item,
                  drillDown: { tipo: 'entidad', valor: item.nombre, etiqueta: item.nombre },
                }))}
              />
            )}

            {tab === 'fecha' && (
              <GrillaTarjetasArqueo
                vacio="No hay movimientos por fecha en el período."
                onDrillDown={onDrillDown}
                items={arqueo.porFecha.map((item: ArqueoCarteraPorFecha) => ({
                  key: item.fecha,
                  titulo: formatDate(item.fecha),
                  accentSeed: item.fecha,
                  grupo: item,
                  drillDown: { tipo: 'fecha', valor: item.fecha, etiqueta: formatDate(item.fecha) },
                }))}
              />
            )}
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArqueoCarteraDialog;
